/*eslint-disable no-unused-vars */
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import { useEffect, useMemo, useRef, useState } from 'react';
import LocalstorageService from '../../helpers/localstorage-service';
import { AuthUserInfo, CompanyId } from '../../constants';
import { Button, Col, Input, Label, Row, Table, UncontrolledTooltip } from 'reactstrap';
import { useOutletContext } from 'react-router-dom';
import { t } from 'i18next';
import { Form, Formik } from 'formik';
import ReactSelect from 'react-select';
import ReactDatePicker from 'react-datepicker';
import NoRecords from '../../components/Table/NoRecords';
import SortableHeader from '../../components/SortableHeader';
import CommonField from '../../components/CommonField';
import AgentService from '../../services/agent';
import reportService from '../../services/report';
import { exportToExcel, exportToPDF } from '../../helpers/exportUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as Yup from 'yup';

const AgentCashExpense = () => {
  const dispatch = useDispatch();
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [agentId, setAgentId] = useState('');
  const reqPayload = {};
  const formRef = useRef();
  let companyId = LocalstorageService.getItem(CompanyId);
  const { company } = LocalstorageService.getItem(AuthUserInfo)?.data;
  const previousMonth = new Date().getMonth() - 1;
  const prviouusMonthDate = new Date();
  prviouusMonthDate.setMonth(previousMonth);
  const [dateState, setDateState] = useState({ start: prviouusMonthDate, end: new Date() });
  const dateRef = useRef();
  const dateRef2 = useRef();
  const [reference, setReference] = useState('');
  const [checkBox, setCheckbox] = useState(false);
  const [downloading, setDownloading] = useState({ export: false, exportExcel: false });
  const [printLoading, setPrintLoading] = useState(null); // reference being loaded for print
  const [excelLoading, setExcelLoading] = useState(null); // reference being loaded for Excel export
  const [sortObj, setSortObj] = useState(null);           // { value, asc } for main list sorting

  const InitialValues = {
    agent: '',
    company,
    search: '',
  };

  const clearDate = () => {
    setDateState({ start: '', end: '' });
  };

  const ValidationSchema = Yup.object().shape({
    agent: Yup.string().required(t(`REPORT.REQUIRED_AGENT`)),
  });

  const [compConf, setCompConf] = useState({
    shown: false,
    msg: t('ALERT.EMPLOYEE_ZERO_AMOUNT_LOAD'),
    confirmFn: () => {
      setCompConf((pre) => ({ ...pre, shown: false }));
    },
  });

  useEffect(() => {
    setPageTitle(t('REPORT.AGENTCASHEXPENSE'));
    setCrumbItem([
      { title: t('APPLICATION_TITLE'), link: '#' },
      { title: t('REPORT.AGENTCASHEXPENSE'), link: '#' },
    ]);
    listFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExpenseList = () => {
    if (agentId) {
      getExpenselist(1);
    }
  };

  function updateSearch(e) {
    setReference(e.target.value?.trim());
  }

  const HeaderList = [
    {
      title: t('REPORT.TABLE_FIELDS.DATE'),
      value: 'date',
      style: { minWidth: '80px', width: '11%' },
      sortable: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.BANK'),
      value: 'bankName',
      style: { minWidth: '65px', width: '10%' },
      sortable: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.BRANCH'),
      value: 'branchName',
      style: { minWidth: '65px', width: '10%' },
      sortable: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.ACC_NUMBER'),
      value: 'accountNumber',
      style: { minWidth: '80px', width: '12%' },
      sortable: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.CHQ_REF'),
      value: 'reference',
      style: { minWidth: '80px', width: '12%' },
      sortable: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.CHQ_AMOUNT'),
      value: 'referenceAmount',
      style: { minWidth: '75px', width: '11%' },
      sortable: true,
      isNumeric: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.RETURN_AMOUNT'),
      value: 'returnFromTerminal',
      style: { minWidth: '75px', width: '12%' },
      sortable: true,
      isNumeric: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.CAHS_EXPENSE'),
      value: 'expenseAmount',
      style: { minWidth: '75px', width: '11%' },
      sortable: true,
      isNumeric: true,
    },
    {
      title: t('REPORT.TABLE_FIELDS.MISS_AMOUNT'),
      value: 'missingAmount',
      style: { minWidth: '75px', width: '9%' },
      sortable: true,
      isNumeric: true,
    },
    {
      title: '',
      value: '',
      style: { minWidth: '60px', width: '7%' },
      sortable: false,
    },
  ];

  // Sorted copy of list — re-computed whenever list or sortObj changes
  const sortedList = useMemo(() => {
    if (!sortObj?.value) return list;
    return [...list].sort((a, b) => {
      const av = a[sortObj.value], bv = b[sortObj.value];
      // Date column
      if (sortObj.value === 'date') {
        const da = av ? new Date(av) : 0, db = bv ? new Date(bv) : 0;
        return sortObj.asc ? da - db : db - da;
      }
      // Numeric columns
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortObj.asc ? av - bv : bv - av;
      }
      // String columns
      return sortObj.asc
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''));
    });
  }, [list, sortObj]);

  async function listFn() {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    await AgentService.listAgent({ ...reqPayload, companyId: companyId })
      .then((response) => {
        const { status, message, dataList } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        const arr = [];
        if (dataList.length > 0) {
          dataList.forEach((val) => {
            arr.push({ value: val.id, label: val.firstName });
          });
        }
        setAgentList(arr || []);

        // Auto-select if company has only one agent
        if (arr.length === 1) {
          setAgentId(arr[0].value);
          setTimeout(() => {
            formRef.current?.setFieldValue('agent', String(arr[0].value));
          }, 0);
        }
      })
      .finally(() => {
        setIsLoading(false);
        dispatch(setLoadingOverlay(false));
      });
  }

  async function getExpenselist(pageNo) {
    reqPayload.companyId = companyId;
    reqPayload.agentId = agentId;
    if (checkBox) {
      reqPayload.referenceAmount = parseInt(reference) || 0;
      reqPayload.reference = '';
    } else {
      reqPayload.referenceAmount = 0;
      reqPayload.reference = reference;
    }
    reqPayload.export = false;
    reqPayload.exportExcel = false;
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    await reportService
      .getAgentExpenses({
        ...reqPayload,
        startDate: dateState.start ? new Date(dateState.start).toLocaleDateString('en-us') : '',
        endDate: dateState.end ? new Date(dateState.end).toLocaleDateString('en-us') : '',
      })
      .then((response) => {
        const { status, message, dataList } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        setList(dataList || []);
      })
      .finally(() => {
        setIsLoading(false);
        dispatch(setLoadingOverlay(false));
      });
  }

  // ── Print check detail ────────────────────────────────────────
  const handlePrintCheck = async (item) => {
    if (!item.reference) {
      toast.warning('אין אסמכתא לשיק זה');
      return;
    }

    // ⚠️ Must open the window SYNCHRONOUSLY (in the click handler, before any await)
    // Otherwise the browser blocks it as a popup.
    const win = window.open('', '_blank', 'width=950,height=950');
    if (!win) {
      toast.warning('חסום חלונות קופצים — אנא אפשר בדפדפן ונסה שוב');
      return;
    }
    win.document.write(
      `<html dir="rtl"><head><meta charset="UTF-8">` +
      `<style>body{font-family:Arial;text-align:center;padding:80px;direction:rtl;color:#555}</style></head>` +
      `<body><h2>⏳ טוען פירוט שיק ${item.reference}...</h2></body></html>`
    );

    setPrintLoading(item.reference);
    try {
      const response = await reportService.getCheckDetail({ companyId, reference: item.reference });
      const { status, dataList } = response.data;
      if (status !== 200 || !dataList?.length) {
        win.document.write(
          `<html dir="rtl"><body style="font-family:Arial;text-align:center;padding:80px;direction:rtl;color:#c00">` +
          `<h2>אין פירוט מסופים לשיק זה</h2></body></html>`
        );
        toast.info('אין פירוט מסופים לשיק זה');
        return;
      }
      // Write full HTML into the already-open (and browser-approved) window
      const html = buildPrintHtml(item, dataList);
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (e) {
      console.error('CheckDetail error:', e);
      win.close();
      toast.error('שגיאה בטעינת פירוט השיק');
    } finally {
      setPrintLoading(null);
    }
  };

  // ── Excel export for a single check (per-terminal detail) ────────────────
  const handleExcelCheck = async (item) => {
    if (!item?.reference) { toast.warning('אין אסמכתא לשיק זה'); return; }
    setExcelLoading(item.reference);
    try {
      const response = await reportService.getCheckDetail({ companyId, reference: item.reference });
      const { status, dataList } = response.data;
      if (status !== 200 || !dataList?.length) {
        toast.info('אין פירוט מסופים לשיק זה'); return;
      }

      // Helper: parse depositBatches → deposit total (number)
      const parseDepTotal = (depositBatches) => {
        if (!depositBatches) return 0;
        return depositBatches.split('@@').reduce((s, dep) => {
          const parts = dep.split(' | ');
          return s + (parseInt((parts[2] || '0').replace(/[^0-9]/g, ''), 10) || 0);
        }, 0);
      };

      // Helper: parse expense sum from expenseDetails
      // Format: "תיאור | שולם ל: X | NNN ₪ | DD/MM/YYYY | LocationName"
      const parseExpenseSum = (details) => {
        if (!details) return 0;
        return details.split('@@').reduce((acc, exp) => {
          const parts = exp.split(' | ');
          return acc + (parseInt((parts[2] || '0').replace(/[^0-9]/g, ''), 10) || 0);
        }, 0);
      };

      const h            = dataList[0];
      const checkRef     = h.reference      || item.reference  || '';
      const checkDate    = h.withdrawalDate || '';
      const agentName    = h.agentName      || '';
      // SP's withdrawnAmount = SUM(RemainedAfterSplit), returnedAmount = SUM(ReturnFromTerminal)
      const checkAmount  = h.withdrawnAmount  || (item.referenceAmount ?? 0);   // 500,000
      const totalReturn  = h.returnedAmount   || 0;                              // 496,000
      const expenseSum   = parseExpenseSum(h.expenseDetails) || (h.totalExpenses ?? 0);
      const gap          = checkAmount - totalReturn - expenseSum;               // 2,400
      const totalLoad    = dataList.reduce((s, r) => s + (r.loadAmount          || 0), 0);
      const totalReturnPerTerm = dataList.reduce((s, r) => s + (r.returnFromTerminal || 0), 0);
      const totalNotYet  = dataList.reduce((s, r) => s + (r.notYetReturned      || 0), 0);
      const fmt          = (n) => Number(n) || 0;  // ensure numeric cell

      // ── Sheet 1: Terminal detail ─────────────────────────────────────────
      // Structure: 2 header rows, empty row, column headers,
      //   then for each terminal: main row + deposit sub-rows, then totals.
      const numFmt    = '#,##0';
      const numFmtRed = '#,##0;[Red]-#,##0';
      const applyFmt = (ws, row, col, fmtStr) => {
        const ref = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = fmtStr;
      };

      const aoa1 = [
        [`שיק: ${checkRef}`, '', `סוכן: ${agentName}`, '', `סכום שיק:`, fmt(checkAmount)],
        [`תאריך: ${checkDate}`, '', `הוצאות מזומן:`, fmt(expenseSum), `פער לסגירה:`, fmt(gap)],
        [],
        ['מספר מסוף', 'מקום', 'אצווה', 'תאריך טעינה', 'עובד', 'סכום טעינה', 'החזר מהמסוף', 'לא חזר עדיין'],
      ];

      // Track row indices for format application
      const mainRowIdxs    = [];  // terminal main rows
      const depositRowIdxs = [];  // deposit sub-rows

      for (const r of dataList) {
        // ── Main terminal row ──────────────────────────────────────────────
        mainRowIdxs.push(aoa1.length);
        aoa1.push([
          r.terminalNumber  || '',
          r.businessName    || '',
          r.batch           || '',
          r.loadDate        || '',
          r.employeeName    || '',
          fmt(r.loadAmount),
          fmt(r.returnFromTerminal || 0),
          fmt(r.notYetReturned     || 0),
        ]);

        // ── Deposit sub-rows (one row per deposit batch) ───────────────────
        // depositBatches format: "DD/MM/YYYY | אצווה XXXX | Y ₪" sep. by @@
        if (r.depositBatches) {
          for (const dep of r.depositBatches.split('@@').filter(d => d.trim())) {
            const p   = dep.split(' | ');
            const amt = parseInt((p[2] || '0').replace(/[^0-9]/g, ''), 10) || 0;
            depositRowIdxs.push(aoa1.length);
            // cols: marker | date+batch | empty×4 | amount | empty
            aoa1.push(['  ↳', `${p[0]} | ${p[1]}`, '', '', '', '', fmt(amt), '']);
          }
        }
      }

      // ── Totals row ────────────────────────────────────────────────────────
      const totalsRowIdx = aoa1.length;
      aoa1.push(['', '', '', '', 'סה"כ:', fmt(totalLoad), fmt(totalReturnPerTerm), fmt(totalNotYet)]);

      const ws1 = XLSX.utils.aoa_to_sheet(aoa1);
      ws1['!cols'] = [6, 22, 12, 14, 18, 13, 14, 14].map((w) => ({ wch: w }));
      ws1['!views'] = [{ RTL: true }];

      // ── Apply number formats ──────────────────────────────────────────────
      applyFmt(ws1, 0, 5, numFmt);      // header: checkAmount
      applyFmt(ws1, 1, 3, numFmt);      // header: expenseSum
      applyFmt(ws1, 1, 5, numFmtRed);   // header: gap
      for (const ri of mainRowIdxs) {
        applyFmt(ws1, ri, 5, numFmt);
        applyFmt(ws1, ri, 6, numFmt);
        applyFmt(ws1, ri, 7, numFmtRed);
      }
      for (const ri of depositRowIdxs) {
        applyFmt(ws1, ri, 6, numFmt);   // deposit amount
      }
      applyFmt(ws1, totalsRowIdx, 5, numFmt);
      applyFmt(ws1, totalsRowIdx, 6, numFmt);
      applyFmt(ws1, totalsRowIdx, 7, numFmtRed);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws1, 'פירוט מסופים');

      // ── Sheet 2: Expense detail (if any) ─────────────────────────────────
      // expenseDetails format: "תיאור | שולם ל: X | NNN ₪ | DD/MM/YYYY | LocationName"
      if (h.expenseDetails) {
        const expRows = h.expenseDetails.split('@@').map((exp) => {
          const parts = exp.split(' | ');
          return [
            parts[0] || '',   // תיאור
            parts[1] || '',   // שולם ל
            fmt(parseInt((parts[2] || '0').replace(/[^0-9]/g, ''), 10)),  // סכום
            parts[3] || '',   // תאריך
            parts[4] || '',   // מקום
          ];
        });
        const aoa2 = [
          ['תיאור', 'שולם ל', 'סכום', 'תאריך', 'מקום'],
          ...expRows,
          ['', 'סה"כ הוצאות:', fmt(expenseSum), '', ''],
        ];
        const ws2 = XLSX.utils.aoa_to_sheet(aoa2);
        ws2['!cols'] = [28, 22, 12, 12, 22].map((w) => ({ wch: w }));
        ws2['!views'] = [{ RTL: true }];
        // Apply thousands format to amount column (col 2) for each data + totals row
        for (let row = 1; row <= expRows.length + 1; row++) {
          applyFmt(ws2, row, 2, numFmt);
        }
        XLSX.utils.book_append_sheet(wb, ws2, 'הוצאות מזומן');
      }

      const filename = `שיק_${item.reference}_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
      toast.success('הקובץ יורד בהצלחה');
    } catch (e) {
      console.error('CheckDetail Excel error:', e);
      toast.error('שגיאה בייצוא לאקסל');
    } finally {
      setExcelLoading(null);
    }
  };

  // Format with comma thousands separator (e.g. 3,400); handle negative separately
  const formatNum = (n) => {
    const v = n ?? 0;
    const abs = Math.abs(v);
    const str = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(abs);
    return (v < 0 ? '-' : '') + str;
  };

  // ₪ LEFT of the number, regular space (NOT &nbsp; — avoids the "·" rendering bug in PDF)
  const shekel = (n) => `<span dir="ltr" style="white-space:nowrap">&#x20AA; ${formatNum(n)}</span>`;

  const buildPrintHtml = (header, rows) => {
    const h = rows[0];

    // ── Helper: parse depositBatches string → individual deposit entries ──────
    // Format per entry: "DD/MM/YYYY | אצווה XXXX | Y ₪"  separated by @@
    const parseDepositBatches = (depositBatches) => {
      if (!depositBatches) return [];
      return depositBatches.split('@@').map((dep) => {
        const parts  = dep.split(' | ');
        return {
          date:   parts[0] || '',
          batch:  (parts[1] || '').replace('אצווה ', ''),
          amount: parseInt((parts[2] || '0').replace(/[^0-9]/g, ''), 10) || 0,
        };
      });
    };

    // depositBatches sub-rows are INFORMATIONAL — for display only.
    // Totals use r.returnFromTerminal (TotalToReturn-based, from SP — authoritative).
    const sumDeposits = (r) => parseDepositBatches(r.depositBatches).reduce((s, d) => s + d.amount, 0);

    // Helper: compute expense total by parsing expenseDetails string (most reliable).
    // Format per entry: "תיאור | שולם ל: X | NNN ₪ | DD/MM/YYYY | LocationName" separated by @@
    const parseExpensesSum = (details) => {
      if (!details) return null;
      const sum = details.split('@@').reduce((acc, exp) => {
        const parts = exp.split(' | ');
        return acc + (parseInt((parts[2] || '0').replace(/[^0-9]/g, ''), 10) || 0);
      }, 0);
      return sum > 0 ? sum : null;
    };

    // Totals — use SP's returnFromTerminal (= loadAmount - TotalToReturn, authoritative).
    // TotalToReturn is maintained by DepositMatchingService and reflects cross-batch credits.
    const totalLoadAmount    = rows.reduce((s, r) => s + (r.loadAmount       || 0), 0);
    const totalReturnFromTrm = rows.reduce((s, r) => s + (r.returnFromTerminal || 0), 0);
    const totalNotYet        = rows.reduce((s, r) => s + (r.notYetReturned   || 0), 0);

    // SP computes withdrawnAmount = SUM(RemainedAfterSplit) — correct for split checks.
    // SP computes returnedAmount = SUM(ReturnFromTerminal) — maintained by BankDeposits service.
    // These are AUTHORITATIVE — use SP values directly for the header.
    const withdrawnAmount = h.withdrawnAmount  || (header.referenceAmount ?? 0);
    const returnedAmount  = h.returnedAmount   || 0;   // 496,000 for check 138241
    // Priority: parsed sum from expenseDetails > SP totalExpenses > list-row expenseAmount
    const totalExpenses   = parseExpensesSum(h.expenseDetails)
                            ?? (h.totalExpenses != null ? h.totalExpenses : null)
                            ?? (header.expenseAmount ?? 0);
    const gap             = withdrawnAmount - returnedAmount - totalExpenses;  // 2,400 ✓

    // Columns: 0=תאריך 1=מסוף 2=מקום 3=עובד 4=אצווה 5=סכום-הטעינה 6=החזר-מהמסוף 7=לא-חזר
    // Deposit sub-row: meta spans cols 0-5 (colspan=6), amount in col 6, empty col 7
    const makeDepositRows = (r) => {
      const entries = parseDepositBatches(r.depositBatches);
      return entries.map(({ date, batch, amount }) => `<tr class="dep-row">
          <td class="dep-meta" colspan="6">&#x21B3; הפקדה ${date} | אצווה <b>${batch}</b></td>
          <td class="num dep">${shekel(amount)}</td>
          <td></td>
        </tr>`).join('');
    };

    const detailRows = rows.map((r) => {
      // Use SP's returnFromTerminal (TotalToReturn-based) — authoritative.
      // depositBatches sub-rows shown as informational only; their sum may differ.
      const returned = r.returnFromTerminal || 0;
      const notYet   = r.notYetReturned     || 0;
      // Red when negative (= more returned than loaded → cross-batch over-attribution)
      const gapClass = notYet < 0 ? 'red' : '';
      const rowClass = notYet > 0 ? 'pending' : '';
      return `
      <tr class="${rowClass}">
        <td>${r.loadDate || ''}</td>
        <td>${r.terminalNumber || ''}</td>
        <td>${r.businessName || ''}</td>
        <td>${r.employeeName || ''}</td>
        <td>${r.batch || ''}</td>
        <td class="num">${shekel(r.loadAmount)}</td>
        <td class="num">${shekel(returned)}</td>
        <td class="num ${gapClass}">${shekel(notYet)}</td>
      </tr>${makeDepositRows(r)}`;
    }).join('');

    // Pending = terminals with notYetReturned > 0, sorted highest first
    const pendingRows = rows
      .filter((r) => (r.notYetReturned || 0) > 0)
      .sort((a, b) => (b.notYetReturned || 0) - (a.notYetReturned || 0));
    const pendingTotalNotYet = pendingRows.reduce((s,r)=>s+(r.notYetReturned||0),0);
    const pendingSection = pendingRows.length > 0 ? `
      <h3 style="margin-top:16px;color:#c00;font-size:12px">⚠️ מסופים שטרם החזירו כסף לבנק</h3>
      <table style="font-size:11px">
        <thead><tr>
          <th>מסוף</th><th>מקום</th><th>עובד</th><th>אצווה</th>
          <th class="num">טעינה</th><th class="num">חזר</th><th class="num">לא חזר</th>
        </tr></thead>
        <tbody>
          ${pendingRows.map((r) => {
            const ny = r.notYetReturned || 0;
            return `<tr>
              <td>${r.terminalNumber}</td>
              <td>${r.businessName}</td>
              <td>${r.employeeName}</td>
              <td>${r.batch}</td>
              <td class="num">${shekel(r.loadAmount)}</td>
              <td class="num">${shekel(r.returnFromTerminal || 0)}</td>
              <td class="num ${ny < 0 ? 'red' : ''}">${shekel(ny)}</td>
            </tr>`;
          }).join('')}
          <tr class="total">
            <td colspan="4" class="label-end">סה"כ:</td>
            <td class="num">${shekel(pendingRows.reduce((s,r)=>s+(r.loadAmount||0),0))}</td>
            <td class="num">${shekel(pendingRows.reduce((s,r)=>s+(r.returnFromTerminal||0),0))}</td>
            <td class="num ${pendingTotalNotYet < 0 ? 'red' : ''}">${shekel(pendingTotalNotYet)}</td>
          </tr>
        </tbody>
      </table>` : `<p style="color:#080;margin-top:10px;font-size:11px">✅ כל הכסף הוחזר לבנק</p>`;

    // Expense breakdown
    // expenseDetails format: "תיאור | שולם ל: X | NNN ₪ | DD/MM/YYYY | LocationName"
    const expenseSection = h.expenseDetails ? (() => {
      const expRows = h.expenseDetails.split('@@').map((exp) => {
        const parts    = exp.split(' | ');
        const note     = parts[0] || '';
        const paid     = parts[1] || '';
        const expRaw   = parseInt((parts[2] || '0').replace(/[^0-9]/g, ''), 10) || 0;
        const date     = parts[3] || '';
        const location = parts[4] || '';
        return `<tr>
          <td>${date}</td><td>${note}</td><td>${paid}</td><td>${location}</td><td class="num">${shekel(expRaw)}</td>
        </tr>`;
      }).join('');
      return `
        <h3 style="margin-top:16px;font-size:12px">💵 פירוט הוצאות מזומן</h3>
        <table>
          <thead><tr><th>תאריך</th><th>תיאור</th><th>שולם ל</th><th>מקום</th><th class="num">סכום</th></tr></thead>
          <tbody>${expRows}</tbody>
          <tfoot>
            <tr class="total">
              <td colspan="4" class="label-end">סה"כ הוצאות:</td>
              <td class="num">${shekel(totalExpenses)}</td>
            </tr>
          </tfoot>
        </table>`;
    })() : '';

    const html = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8">
  <title>טעינות והפקדות לפי שיק — ${header.reference || h.reference}</title>
  <style>
    @page { size: A4 portrait; margin: 15mm 12mm; }
    * { box-sizing: border-box; font-family: Arial, Helvetica, sans-serif; }
    body { font-size: 11px; color: #222; direction: rtl; }
    h2 { text-align: center; font-size: 15px; margin-bottom: 4px; }
    .sub { text-align: center; color: #555; font-size: 10px; margin-bottom: 12px; }
    .header-grid {
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 4px 12px; border: 1px solid #bbb; border-radius: 4px;
      padding: 8px 12px; margin-bottom: 14px; background: #f7f7f7;
    }
    .header-grid div { display: flex; flex-direction: column; }
    .header-grid .lbl { font-size: 9px; color: #888; }
    .header-grid .val { font-weight: bold; font-size: 12px; }
    .gap-warn { color: #c00; } .gap-ok { color: #080; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 6px; table-layout: fixed; }
    th, td { border: 1px solid #ccc; padding: 3px 5px; font-size: 10px; overflow: hidden; }
    /* Column widths for the main terminal table (8 cols, A4 portrait ~186mm usable) */
    col.c-date  { width: 18mm; }
    col.c-trm   { width: 18mm; }
    col.c-place { width: 34mm; }
    col.c-emp   { width: 26mm; }
    col.c-batch { width: 22mm; }
    col.c-load  { width: 22mm; }
    col.c-ret   { width: 22mm; }
    col.c-gap   { width: 22mm; }
    thead th { background: #333; color: #fff; white-space: nowrap; }
    tfoot td { background: #eee; font-weight: bold; }
    tr.grand-total td { background: #eee; font-weight: bold; border-top: 2px solid #999; }
    .num { text-align: left; direction: ltr; white-space: nowrap; }
    .red { color: #c00; font-weight: bold; }
    .green-txt { color: #080; }
    tr.pending { background: #fff3f3; }
    .label-end { text-align: left; }
    tr.dep-row td { background: #eef2ff; font-size: 9px; color: #334; border-top: none; padding: 2px 6px; }
    td.dep-meta { color: #446; }
    td.dep { color: #224; font-style: italic; white-space: nowrap; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h2>טעינות והפקדות לפי שיק</h2>
  <div class="sub">הופק: ${new Date().toLocaleDateString('he-IL')}</div>

  <div class="header-grid">
    <div><span class="lbl">תאריך משיכה</span>
         <span class="val">${h.withdrawalDate || (header.date ? new Date(header.date).toLocaleDateString('he-IL') : '')}</span></div>
    <div><span class="lbl">שם סוכן</span><span class="val">${h.agentName || ''}</span></div>
    <div><span class="lbl">מספר שיק / אסמכתא</span><span class="val">${h.reference || header.reference || ''}</span></div>
    <div><span class="lbl">סכום שיק</span><span class="val">${shekel(withdrawnAmount)}</span></div>
    <div><span class="lbl">סה"כ שחזר לבנק</span><span class="val">${shekel(returnedAmount)}</span></div>
    <div><span class="lbl">הוצאות מזומן</span><span class="val">${shekel(totalExpenses)}</span></div>
    <div>
      <span class="lbl">פער לסגירה</span>
      <span class="val ${gap !== 0 ? 'gap-warn' : 'gap-ok'}">${gap !== 0 ? '⚠️ ' : '✅ '}${shekel(Math.abs(gap))}</span>
    </div>
  </div>

  <h3 style="margin-bottom:4px;font-size:12px">פירוט מסופים</h3>
  <table>
    <colgroup>
      <col class="c-date"><col class="c-trm"><col class="c-place"><col class="c-emp">
      <col class="c-batch"><col class="c-load"><col class="c-ret"><col class="c-gap">
    </colgroup>
    <thead>
      <tr>
        <th>תאריך</th><th>מספר מסוף</th><th>מקום</th><th>עובד</th><th>אצווה</th>
        <th class="num">סכום הטעינה</th>
        <th class="num">החזר מהמסוף</th>
        <th class="num">לא חזר עדיין</th>
      </tr>
    </thead>
    <tbody>
      ${detailRows}
      <tr class="grand-total">
        <td colspan="5" class="label-end">סה"כ:</td>
        <td class="num">${shekel(totalLoadAmount)}</td>
        <td class="num">${shekel(totalReturnFromTrm)}</td>
        <td class="num ${totalNotYet < 0 ? 'red' : ''}">${shekel(totalNotYet)}</td>
      </tr>
    </tbody>
  </table>

  ${pendingSection}
  ${expenseSection}

  <script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;

    return html;
  };

  // ── Export helpers ────────────────────────────────────────────
  const agentExpenseColumns = [
    { key: 'date', header: 'תאריך', type: 'date' },
    { key: 'bankName', header: 'בנק' },
    { key: 'branchName', header: 'סניף' },
    { key: 'accountNumber', header: 'מספר חשבון' },
    { key: 'reference', header: 'אסמכתא' },
    { key: 'referenceAmount', header: 'סכום שיק' },
    { key: 'returnFromTerminal', header: 'החזר' },
    { key: 'expenseAmount', header: 'הוצאות' },
    { key: 'missingAmount', header: 'חסר' },
  ];

  function handleDownload(selectedType) {
    if (list.length === 0) {
      toast.warning(t('REPORT.NO_DATA_TO_EXPORT') || 'אין נתונים לייצוא');
      return;
    }

    setDownloading({ export: selectedType.export, exportExcel: selectedType.exportExcel });

    try {
      const filename = `AgentExpenseReport_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}`;
      const title = t('REPORT.AGENTCASHEXPENSE') || 'דוח הוצאות סוכן';

      if (selectedType.exportExcel) {
        const success = exportToExcel(list, agentExpenseColumns, filename, title);
        if (success) toast.success(t('REPORT.EXPORT_SUCCESS') || 'הקובץ יורד בהצלחה');
        else toast.error(t('REPORT.EXPORT_FAILED') || 'שגיאה בייצוא');
      } else {
        const success = exportToPDF(list, agentExpenseColumns, filename, title);
        if (success) toast.success(t('REPORT.EXPORT_SUCCESS') || 'הקובץ יורד בהצלחה');
        else toast.error(t('REPORT.EXPORT_FAILED') || 'שגיאה בייצוא');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('REPORT.EXPORT_FAILED') || 'שגיאה בייצוא');
    } finally {
      setDownloading({ export: false, exportExcel: false });
    }
  }

  // Row background: red when missingAmount ≠ 0 (discrepancy even after returns)
  const rowClass = (item) => {
    const missing = item?.missingAmount ?? 0;
    if (missing !== 0) return 'table-danger';
    return '';
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={InitialValues}
        enableReinitialize={true}
        validationSchema={ValidationSchema}
        onSubmit={fetchExpenseList}
      >
        {({ values, setFieldValue, setFieldTouched, handleSubmit, errors }) => (
          <Form noValidate>
            <Row className=" gap-2">
              <Col sm={3}>
                <div className="mb-3 mt-2">
                  <CommonField
                    fieldType="string"
                    fieldName="company"
                    label={t('REPORT.COMPANY_LABEL')}
                    isDisabled={true}
                  />
                </div>
              </Col>
              <Col sm={3}>
                <div className=" mb-3">
                  <label htmlFor="agent">{t('REPORT.AGENT_LABEL') + '*'}</label>
                  <ReactSelect
                    name={'agent'}
                    id="agent"
                    value={!agentId ? '' : agentList.filter(({ value }) => value === agentId)}
                    options={agentList}
                    className="react-select"
                    onChange={(option) => {
                      setFieldValue('agent', option?.value);
                      setAgentId(option?.value);
                    }}
                    onBlur={() => setFieldTouched('agent', true)}
                    placeholder={`-- ${t('REPORT.AGENT_PLACEHOLDER')} --`}
                    noOptionsMessage={() => t('RSEL_NO_OPT')}
                    isSearchable={true}
                  />
                </div>
                {errors.agent && (
                  <div className="errorMSG" style={{ color: 'red' }}>
                    {errors.agent}
                  </div>
                )}
              </Col>
              <Col sm={5} className="d-flex mb-1">
                <div></div>
                <div className="mb-3 gap-2 d-flex align-items-end">
                  <Button
                    color="success"
                    type="button"
                    size="sm"
                    disabled={isLoading || list.length === 0}
                    onClick={() => handleDownload({ export: true, exportExcel: true })}
                  >
                    📥 {downloading.export && downloading.exportExcel ? t('REPORT.DOWNLOADING') : t('REPORT.DOWNLOAD_XLX')}
                  </Button>
                  <Button
                    color="danger"
                    type="button"
                    size="sm"
                    disabled={isLoading || list.length === 0}
                    onClick={() => handleDownload({ export: true, exportExcel: false })}
                  >
                    📄 {downloading.export && !downloading.exportExcel
                      ? t('REPORT.DOWNLOADING')
                      : t('REPORT.DOWNLOAD_PDF')}
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="mb-4 ">
              <Col sm={3} className="mt-3">
                <div className="d-flex justify-content-around align-items-center">
                  <label htmlFor="dateStart" className="text-nowrap" style={{ width: '40%' }}>
                    {t(`LOAD_REMARKS.FORM_DATE`)}
                  </label>
                  <ReactDatePicker
                    ref={dateRef}
                    name="dateStart"
                    id="dateStart"
                    className="form-control"
                    selected={dateState.start}
                    onChange={(dateArr) =>
                      setDateState((prev) => ({ ...prev, start: dateArr }))
                    }
                    highlightDates={[new Date()]}
                    dateFormat={'dd/MM/yyyy'}
                    shouldCloseOnSelect={true}
                  />
                </div>
              </Col>
              <Col sm={3} className="mb-6 mt-3">
                <div className="d-flex justify-content-around align-items-center">
                  <label htmlFor="dateEnd" style={{ width: '40%' }}>
                    {t(`LOAD_REMARKS.TO_DATE`)}
                  </label>
                  <ReactDatePicker
                    ref={dateRef2}
                    name="dateEnd"
                    id="dateEnd"
                    className="form-control"
                    selected={dateState.end}
                    onChange={(dateArr) =>
                      setDateState((prev) => ({ ...prev, end: dateArr }))
                    }
                    highlightDates={[new Date()]}
                    dateFormat={'dd/MM/yyyy'}
                    shouldCloseOnSelect={true}
                  />
                  <Button
                    className="ms-2 "
                    id="clearBtn"
                    color="danger"
                    disabled={!dateState.start && !dateState.end}
                    type="button"
                    onClick={() => clearDate()}
                  >
                    {'X'}
                    <UncontrolledTooltip placement="right" target={`clearBtn`}>
                      {t(`REPORT.CLEAR_DATE`)}
                    </UncontrolledTooltip>
                  </Button>
                </div>
              </Col>
              <Col sm={3} className="d-flex mt-3 ">
                <div style={{ width: '50%' }}></div>
                <div className="d-flex gap-2 my-2">
                  <Input
                    id="selectSearchType"
                    type="checkbox"
                    onChange={(e) => {
                      setFieldValue('search', '');
                      setReference('');
                      setCheckbox(e.target.checked);
                    }}
                    checked={checkBox}
                  />
                  <Label htmlFor="selectSearchType" className="mb-0 ">
                    {t('REPORT.SEARCH_PLACEHOLDER2')}
                  </Label>
                </div>
              </Col>
              <Col sm={3}>
                <div className="d-flex mt-3">
                  <Input
                    name="search"
                    type={checkBox ? 'number' : 'text'}
                    onChange={(e) => {
                      updateSearch(e);
                      setFieldValue('search', e.target.value.trim());
                    }}
                    value={values.search}
                    onKeyUp={({ code }) => {
                      if (code === 'Enter') handleSubmit();
                    }}
                    onWheel={(e) => e.target.blur()}
                    placeholder={checkBox ? t(`REPORT.SEARCH_PLACEHOLDER2`) : t('REPORT.SEARCH_PLACEHOLDER')}
                  />
                  <Button className="ms-2" color="primary" type="button" onClick={() => handleSubmit()}>
                    <i className="ri-search-line"></i>
                  </Button>
                </div>
                {errors.search && (
                  <div style={{ color: 'red' }}>
                    {checkBox ? t('REPORT.REQUIRED_PLACE2') : t('REPORT.REQUIRED_PLACE1')}
                  </div>
                )}
              </Col>
            </Row>

            {/* Legend */}
            {list.length > 0 && (
              <div className="mb-2 d-flex gap-3 small text-muted flex-wrap">
                <span>
                  <span className="badge bg-danger">■</span> אי-התאמה — הסכום שחזר + הוצאות ≠ סכום השיק
                </span>
                <span className="text-info">💡 לחץ 🖨️ לדוח מפורט לפי מסוף</span>
              </div>
            )}

            <div className="dataMin reportTable">
              <Table
                className="fixTh dataTable "
                striped
                bordered
                responsive
                size="sm"
                style={{ minWidth: '1000px' }}
              >
                <thead>
                  <tr>
                    <SortableHeader
                      showActionCol={false}
                      headers={HeaderList}
                      isDisabled={list.length === 0}
                      sortClick={(obj) => setSortObj(obj)}
                    />
                  </tr>
                </thead>

                <tbody>
                  {sortedList.map((item, index) => (
                    <tr key={index} className={rowClass(item)}>
                      <td className="text-nowrap">
                        {item?.date ? new Date(item.date)?.toLocaleDateString('en-GB') : ''}
                      </td>
                      <td dir="ltr">{item.bankName}</td>
                      <td>{item.branchName}</td>
                      <td>{item.accountNumber}</td>
                      <td>{item.reference}</td>
                      <td dir="ltr">{item.referenceAmount?.toLocaleString('en-US')}</td>
                      <td dir="ltr">{item.returnFromTerminal?.toLocaleString('en-US')}</td>
                      <td dir="ltr">{item.expenseAmount?.toLocaleString('en-US')}</td>
                      <td dir="ltr" className={item.missingAmount !== 0 ? 'text-danger fw-bold' : 'text-success'}>
                        {item.missingAmount?.toLocaleString('en-US')}
                      </td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          color="info"
                          size="sm"
                          outline
                          className="me-1"
                          disabled={printLoading === item.reference}
                          onClick={() => handlePrintCheck(item)}
                          title="הדפסת דוח מפורט לשיק זה"
                        >
                          {printLoading === item.reference ? '...' : '🖨️'}
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          outline
                          disabled={excelLoading === item.reference}
                          onClick={() => handleExcelCheck(item)}
                          title="ייצוא פירוט שיק לאקסל"
                        >
                          {excelLoading === item.reference ? '...' : '📊'}
                        </Button>
                      </td>
                    </tr>
                  ))}

                  {(isLoading || sortedList.length === 0) && <NoRecords colSpan={HeaderList.length + 1} />}
                </tbody>
              </Table>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AgentCashExpense;
